/* eslint-disable no-unused-expressions */
import React, { useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import NewsfeedButton from '../Newsfeed/Button';
import Tiptap from '../Tiptap/Tiptap';
import GroupedMultiSelect from '../GroupedMultiSelect/GroupedMultiSelect';
import BlogTagsField from './BlogTagsField';
import HeaderImageInput from './HeaderImageInput';
import { displayToast } from '../../Util/Toast';

export const BLOG_ARTICLE_DRAFT_KEY = 'blogArticleDraft';

const readDraft = () => {
  try {
    return (
      JSON.parse(window.sessionStorage.getItem(BLOG_ARTICLE_DRAFT_KEY)) || {}
    );
  } catch (e) {
    return {};
  }
};

const buildCategoryTree = (categories) => {
  const roots = [];
  const parentsByDepth = [];

  categories.forEach((category) => {
    const depth = Number(category.depth) || 0;
    const option = {
      value: category.id.toString(),
      label: category.name,
      children: []
    };

    parentsByDepth.length = depth;
    const parent = depth > 0 ? parentsByDepth[depth - 1] : null;
    if (parent) {
      parent.children.push(option);
    } else {
      roots.push(option);
    }
    parentsByDepth[depth] = option;
  });

  return roots;
};

const normalizeSelectedCategoryIds = (categoryTree, categoryIds) => {
  const availableIds = new Set();

  const collectAvailableIds = (category) => {
    availableIds.add(category.value);
    category.children.forEach(collectAvailableIds);
  };

  categoryTree.forEach(collectAvailableIds);

  return Array.from(
    new Set(
      (categoryIds || [])
        .map((categoryId) => {
          return categoryId.toString();
        })
        .filter((categoryId) => {
          return availableIds.has(categoryId);
        })
    )
  ).slice(0, 1);
};

const normalizeSelectedSiteCodes = (siteCodes, availableSiteCodes) => {
  const availableCodes = new Set(availableSiteCodes);
  return Array.from(
    new Set(
      (siteCodes || []).filter((siteCode) => {
        return availableCodes.has(siteCode);
      })
    )
  );
};

const BlogArticleModalView = ({
  posting,
  onSubmit = () => {},
  type = 'add',
  blogArticle = null,
  isTranslation = false,
  urlSlugReadOnly = false,
  selectedLanguage = 'default',
  availableLanguages = [],
  categories = [],
  authors = [],
  sites = [],
  translations = {},
  imageUrl = null,
  imageAltText = '',
  onLanguageChange = () => {},
  onCategoryChange = () => {},
  blogTagSearchUri = '',
  mostUsedTags = []
}) => {
  const isEdit = type === 'edit';
  const [draft] = useState(() => {
    return isEdit ? {} : readDraft();
  });
  const initial = isEdit ? blogArticle || {} : draft;

  //Derived selector data
  const siteOptions = useMemo(() => {
    return [...sites]
      .sort((siteA, siteB) => {
        const nameA = siteA.name || '';
        const nameB = siteB.name || '';

        const nameComparison = nameA.localeCompare(nameB, undefined, {
          sensitivity: 'base'
        });

        if (nameComparison !== 0) {
          return nameComparison;
        }

        return (siteA.siteCode || '').localeCompare(siteB.siteCode || '');
      })
      .map((site) => {
        return {
          value: site.siteCode,
          label: `${site.name} (${site.siteCode})`
        };
      });
  }, [sites]);

  const allSiteCodes = useMemo(() => {
    return siteOptions.map((site) => site.value);
  }, [siteOptions]);

  const categoryTree = buildCategoryTree(categories);
  const [title, setTitle] = useState(initial.title || '');
  const [subTitle, setSubTitle] = useState(initial.subTitle || '');
  const [body, setBody] = useState(initial.body || '');
  const [blogAuthorId, setBlogAuthorId] = useState(
    initial.blogAuthorId ? String(initial.blogAuthorId) : ''
  );
  const [urlSlug, setUrlSlug] = useState(initial.urlSlug || '');
  const [seoTitle, setSeoTitle] = useState(initial.seoTitle || '');
  const [seoDescription, setSeoDescription] = useState(
    initial.seoDescription || ''
  );
  const [seoKeywords, setSeoKeywords] = useState(initial.seoKeywords || '');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => {
    return normalizeSelectedCategoryIds(categoryTree, initial.categoryIds);
  });
  const [selectedSiteCodes, setSelectedSiteCodes] = useState(() => {
    if (Array.isArray(initial.sitesCodes)) {
      return normalizeSelectedSiteCodes(initial.sitesCodes, allSiteCodes);
    }
    //default for newly created articles.
    return isEdit ? [] : allSiteCodes;
  });
  const [tags, setTags] = useState(initial.tags || []);
  const [resetKey, setResetKey] = useState(0);
  const [activeTab, setActiveTab] = useState('content');
  const [image, setImage] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [altText, setAltText] = useState(imageAltText || '');

  const SEO_DESCRIPTION_MAX = 160;

  useEffect(() => {
    if (isEdit) {
      return;
    }
    window.sessionStorage.setItem(
      BLOG_ARTICLE_DRAFT_KEY,
      JSON.stringify({
        title,
        subTitle,
        body,
        blogAuthorId,
        seoTitle,
        seoDescription,
        seoKeywords,
        categoryIds: selectedCategoryIds,
        tags,
        sitesCodes: selectedSiteCodes
      })
    );
  }, [
    isEdit,
    title,
    subTitle,
    body,
    blogAuthorId,
    seoTitle,
    seoDescription,
    seoKeywords,
    selectedCategoryIds,
    tags,
    selectedSiteCodes
  ]);

  const resetFields = () => {
    setTitle('');
    setSubTitle('');
    setBody('');
    setBlogAuthorId('');
    setSeoTitle('');
    setSeoDescription('');
    setSeoKeywords('');
    setSelectedCategoryIds([]);
    onCategoryChange([]);
    setTags([]);
    setResetKey((key) => {
      return key + 1;
    });
    setSelectedSiteCodes(allSiteCodes);
  };

  const handleCategoryChange = (categoryIds) => {
    const singleCategoryId = categoryIds.slice(0, 1);
    setSelectedCategoryIds(singleCategoryId);
    onCategoryChange(singleCategoryId);
  };

  const hideModal = () => {
    const modalWrapper = window.document.querySelector('.modal-wrapper');
    if (modalWrapper) modalWrapper.classList.add('hidden');
  };

  const isBodyEmpty = (html) => {
    const text = String(html || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return text.length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isBodyEmpty(body)) {
      displayToast('Body is required', 'error');
      return;
    }
    if (!isTranslation && selectedCategoryIds.length !== 1) {
      displayToast('A category is required', 'error');
      return;
    }
    onSubmit({
      title: (title || '').trim(),
      subTitle: (subTitle || '').trim(),
      body: body || '',
      blogAuthorId: blogAuthorId || null,
      urlSlug: (urlSlug || '').trim(),
      seoTitle: (seoTitle || '').trim(),
      seoDescription: (seoDescription || '').trim(),
      seoKeywords: (seoKeywords || '').trim(),
      image,
      imageChanged,
      imageAltText: (altText || '').trim(),
      categoryIds: selectedCategoryIds,
      tags,
      sitesCodes: selectedSiteCodes
    });
  };

  const handleImageChange = (file) => {
    setImage(file);
    setImageChanged(true);
  };

  return (
    <div
      tabIndex="-1"
      className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 w-full md:inset-0 h-full md:h-screen flex justify-center items-start bg-white bg-opacity-95"
      role="dialog"
    >
      <button
        type="button"
        onClick={hideModal}
        className="absolute top-3 right-5 bg-blue-50 w-12 h-12 rounded-full group z-10"
      >
        <span className="material-icons text-xl text-gray-500 group-hover:text-gray-700">
          close
        </span>
      </button>

      <div
        className="bg-white p-8 h-full md:h-screen overflow-y-auto"
        style={{ width: '800px', maxWidth: '100%' }}
      >
        <div className="mb-6">
          <h1 className="text-xl text-center font-bold">
            {isEdit ? 'Edit Blog Article' : 'Add Blog Article'}
          </h1>
        </div>

        {isEdit && availableLanguages.length > 0 && (
          <div className="mb-4">
            <label
              htmlFor="blog-language"
              className="font-semibold text-gray-800 text-base"
            >
              Language version
              <select
                id="blog-language"
                className="text-left p-3 border w-full border-gray-300 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                value={selectedLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
              >
                <option value="default">Original</option>
                {availableLanguages.map((lang) => {
                  const translated = Boolean(translations[lang.code]);
                  return (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                      {translated ? '' : ' — not translated yet'}
                    </option>
                  );
                })}
              </select>
            </label>
            <p className="text-sm text-gray-500 mt-1">
              {isTranslation
                ? 'Editing this translation — saving updates only this language. It will be overwritten if you re-save the Original.'
                : 'This is the source content. Saving the Original automatically (re)translates the article into all supported languages (Chinese excluded), overwriting manual edits to other languages.'}
            </p>
            {isTranslation && !translations[selectedLanguage] && (
              <p className="text-sm text-amber-600 mt-1">
                Not translated yet — fill in the fields and save to create this
                language, or re-save the Original to auto-translate it.
              </p>
            )}
          </div>
        )}

        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 font-semibold text-base ${
              activeTab === 'content'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Content
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('seo')}
            className={`px-4 py-2 font-semibold text-base ${
              activeTab === 'seo'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            SEO
          </button>
          {!isTranslation && (
            <button
              type="button"
              onClick={() => {
                return setActiveTab('tags');
              }}
              className={`px-4 py-2 font-semibold text-base ${
                activeTab === 'tags'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tags
            </button>
          )}

          {!isTranslation && (
            <button
              type="button"
              onClick={() => setActiveTab('sites')}
              className={`px-4 py-2 font-semibold text-base ${
                activeTab === 'sites'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Sites
            </button>
          )}
        </div>

        <form
          onSubmit={handleFormSubmit}
          className="flex w-full flex-col gap-3"
        >
          <div className={activeTab === 'content' ? '' : 'hidden'}>
            <div className="flex flex-col gap-3">
              <label
                htmlFor="blog-title"
                className="font-semibold text-gray-800 text-lg"
              >
                Title
                <input
                  id="blog-title"
                  type="text"
                  maxLength={255}
                  className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                  onChange={(e) => {
                    return setTitle(e.target.value);
                  }}
                  value={title}
                  required
                />
              </label>

              <label
                htmlFor="blog-sub-title"
                className="font-semibold text-gray-800 text-lg"
              >
                Sub title
                <input
                  id="blog-sub-title"
                  type="text"
                  maxLength={255}
                  className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                  onChange={(e) => {
                    return setSubTitle(e.target.value);
                  }}
                  value={subTitle}
                  required
                />
              </label>

              {!isTranslation && (
                <label
                  htmlFor="blog-author"
                  className="font-semibold text-gray-800 text-lg"
                >
                  Author
                  <select
                    id="blog-author"
                    className="text-left p-3 border w-full border-gray-300 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal bg-white"
                    onChange={(e) => {
                      return setBlogAuthorId(e.target.value);
                    }}
                    value={blogAuthorId}
                    required
                  >
                    <option value="">Select an author</option>
                    {authors.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {!isTranslation && (
                <GroupedMultiSelect
                  label="Blog category"
                  placeholder="Select a blog category"
                  options={categoryTree}
                  selectedValues={selectedCategoryIds}
                  onChange={handleCategoryChange}
                  singleSelect
                />
              )}

              {isEdit && (
                <label
                  htmlFor="blog-url-slug"
                  className="font-semibold text-gray-800 text-lg"
                >
                  Url slug
                  <input
                    id="blog-url-slug"
                    type="text"
                    maxLength={255}
                    className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                    onChange={(e) => {
                      return setUrlSlug(e.target.value);
                    }}
                    value={urlSlug}
                    readOnly={urlSlugReadOnly}
                    required
                  />
                  {urlSlugReadOnly && (
                    <span className="block text-sm font-normal mt-1">
                      Chinese translations use the original English URL slug.
                    </span>
                  )}
                </label>
              )}

              {!isTranslation && (
                <HeaderImageInput
                  initialImageUrl={imageUrl}
                  altText={altText}
                  onImageChange={handleImageChange}
                  onAltTextChange={setAltText}
                />
              )}

              <span className="font-semibold text-gray-800 text-lg">Body</span>
              <div id="blog-body" tabIndex={-1}>
                <Tiptap
                  key={resetKey}
                  content={body}
                  contentClassName="min-h-[200px]"
                  updateContent={(v) => {
                    return setBody(v);
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className={activeTab === 'seo' ? 'flex flex-col gap-3' : 'hidden'}
          >
            <label
              htmlFor="blog-seo-title"
              className="font-semibold text-gray-800 text-lg"
            >
              SEO title
              <input
                id="blog-seo-title"
                type="text"
                maxLength={255}
                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                onChange={(e) => setSeoTitle(e.target.value)}
                value={seoTitle}
              />
            </label>

            <label
              htmlFor="blog-seo-description"
              className="font-semibold text-gray-800 text-lg"
            >
              SEO meta description
              <span
                className={`ml-2 text-sm font-normal ${
                  seoDescription.length > SEO_DESCRIPTION_MAX
                    ? 'text-red-500'
                    : 'text-gray-400'
                }`}
              >
                {seoDescription.length}/{SEO_DESCRIPTION_MAX}
              </span>
              <textarea
                id="blog-seo-description"
                maxLength={SEO_DESCRIPTION_MAX}
                rows={3}
                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal resize-none"
                onChange={(e) => setSeoDescription(e.target.value)}
                value={seoDescription}
              />
            </label>

            <label
              htmlFor="blog-seo-keywords"
              className="font-semibold text-gray-800 text-lg"
            >
              SEO keywords
              <span className="ml-2 text-sm font-normal text-gray-400">
                comma separated
              </span>
              <input
                id="blog-seo-keywords"
                type="text"
                className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
                onChange={(e) => setSeoKeywords(e.target.value)}
                value={seoKeywords}
              />
            </label>
          </div>

          {!isTranslation && (
            <div className={activeTab === 'tags' ? '' : 'hidden'}>
              <BlogTagsField
                tags={tags}
                onChange={setTags}
                searchUri={blogTagSearchUri}
                mostUsedTags={mostUsedTags}
              />
            </div>
          )}

          {!isTranslation && (
            <div className={activeTab === 'sites' ? '' : 'hidden'}>
              <GroupedMultiSelect
                label="Enabled on sites"
                placeholder={`0 of ${siteOptions.length} sites selected`}
                options={siteOptions}
                selectedValues={selectedSiteCodes}
                onChange={setSelectedSiteCodes}
                onSelectAll={() => {
                  setSelectedSiteCodes(allSiteCodes);
                }}
                onClearAll={() => {
                  setSelectedSiteCodes([]);
                }}
                formatSelectedCount={(count) => {
                  return `${count} of ${siteOptions.length} ${
                    siteOptions.length === 1 ? 'site' : 'sites'
                  } selected`;
                }}
              />

              <p className="mt-2 text-sm text-gray-500">
                This article will only be visible on the selected sites.
              </p>
            </div>
          )}

          <div className="w-full flex items-center justify-between gap-2 mt-2">
            {!isEdit ? (
              <button
                type="button"
                className="rounded-md border border-gray-300 text-gray-700 font-semibold text-base px-4 py-2"
                onClick={resetFields}
              >
                Reset
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-md bg-gray-200 text-gray-800 font-semibold text-base px-4 py-2"
                onClick={hideModal}
              >
                Cancel
              </button>
              <NewsfeedButton
                classNames="md:w-auto w-full"
                loading={posting}
                title="Save"
                type="primary"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogArticleModalView;

BlogArticleModalView.propTypes = {
  posting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func,
  type: PropTypes.oneOf(['add', 'edit']),
  blogArticle: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string,
    body: PropTypes.string,
    blogAuthorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    urlSlug: PropTypes.string,
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    seoKeywords: PropTypes.string,
    categoryIds: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    tags: PropTypes.arrayOf(PropTypes.string),
    sitesCodes: PropTypes.arrayOf(PropTypes.string)
  }),
  isTranslation: PropTypes.bool,
  urlSlugReadOnly: PropTypes.bool,
  selectedLanguage: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string
    })
  ),
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string
    })
  ),
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      depth: PropTypes.number.isRequired
    })
  ),
  sites: PropTypes.arrayOf(
    PropTypes.shape({
      siteCode: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  translations: PropTypes.object,
  onLanguageChange: PropTypes.func,
  imageUrl: PropTypes.string,
  imageAltText: PropTypes.string,
  onLanguageChange: PropTypes.func,
  onCategoryChange: PropTypes.func,
  blogTagSearchUri: PropTypes.string,
  mostUsedTags: PropTypes.arrayOf(PropTypes.string)
};
